import { useEffect, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Crosshair,
  Download,
  Eye,
  Folder,
  FolderPlus,
  Map,
  Plus,
  Printer,
  ScanSearch,
  Trash2,
  Upload,
  ChevronDown,
} from "lucide-react";
import Header from "@/components/Header";
import MainWorkspaceLayout from "@/components/workspace/MainWorkspaceLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { rdRequestsApi } from "@/services/rd-requests.service";
import { objectsApi } from "@/services/api-v2.service";
import type {
  RdMapGeometryCreatePayload,
  RdRequestCreatePayload,
  RdRequestBowPatchPayload,
  RdRequestPatchPayload,
} from "@/types/rd-request";
import { EntityListPanel } from "@/components/shared/EntityListPanel";
import { EntityDetailsShell } from "@/components/shared/EntityDetailsShell";
import { ModuleRegistryTitle } from "@/components/shared/ModuleRegistryTitle";
import { notifyModuleError, notifyModuleSuccess } from "@/lib/module-toast";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RdWorkspaceMapPanel } from "@/components/rd/RdWorkspaceMapPanel";
import type { Geometry, GeometryAnalyzeResponse } from "@/types/api-v2";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { parseRdGeometryFromText, type RdCoordinateOrder } from "@/lib/rd-geometry-import";
import {
  downloadGeometryCoordsTemplate,
  GEOMETRY_COORDS_GEOJSON_TEMPLATE,
  GEOMETRY_COORDS_TXT_TEMPLATE_LATLON,
  GEOMETRY_COORDS_TXT_TEMPLATE_LONLAT,
} from "@/lib/geometry-coords-import";
import { printRdIntersectionReport } from "@/lib/rd-intersection-report-print";
import { RdDynamicCardRenderer } from "@/components/rd/RdDynamicCardRenderer";
import { DocumentParseDialog } from "@/components/DocumentParseDialog";
import { GeoContextPanel } from "@/components/go/GeoContextPanel";
import { goEntitiesApi } from "@/services/go-entities.service";

function formatDate(v?: string | null): string {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString("ru-RU");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function geometryTypeBadge(type?: string | null): { label: string; className: string } {
  const v = String(type || "").toLowerCase();
  if (v.includes("point")) return { label: "● POINT", className: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200" };
  if (v.includes("line")) return { label: "— LINE", className: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200" };
  if (v.includes("polygon")) return { label: "▰ POLYGON", className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200" };
  return { label: `◦ ${(type || "N/A").toUpperCase()}`, className: "bg-muted text-muted-foreground" };
}

/** Контур из drawn_geojson РД для подсветки на карте workspace. */
function geometryHighlightFromRdDrawn(
  requestMapGeometryId: number,
  mapGeometryName: string | null | undefined,
  drawn: GeoJSON.Feature | GeoJSON.Geometry,
): Geometry {
  const feature: GeoJSON.Feature =
    drawn.type === "Feature"
      ? drawn
      : { type: "Feature", properties: {}, geometry: drawn };
  const gt = feature.geometry?.type ?? "Polygon";
  const primitive =
    gt === "Point"
      ? "Point"
      : gt === "LineString" || gt === "MultiLineString"
        ? "LineString"
        : "Polygon";
  return {
    id: requestMapGeometryId,
    object_id: 0,
    name: mapGeometryName?.trim() || `Контур РД #${requestMapGeometryId}`,
    geojson: feature,
    type: primitive,
    style: {},
    created_at: new Date().toISOString(),
  };
}

export default function RdWorkspacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") || "");
  const [requestTypeFilter, setRequestTypeFilter] = useState(() => searchParams.get("type") || "all");
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("general");
  const [materialDescription, setMaterialDescription] = useState("");
  const [folderTrail, setFolderTrail] = useState<Array<{ id: number | null; name: string }>>([
    { id: null, name: "Корень" },
  ]);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ nodeId: number; name: string; url: string } | null>(null);
  const [previewImageIds, setPreviewImageIds] = useState<number[]>([]);
  const [rdMapDrawing, setRdMapDrawing] = useState(false);
  const [mapPreviewGeometry, setMapPreviewGeometry] = useState<Geometry | null>(null);
  const [mapPreviewNonce, setMapPreviewNonce] = useState(0);
  const [rdMapCollapsed, setRdMapCollapsed] = useState(false);
  const rdMapPanelRef = useRef<ImperativePanelHandle>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) return;
    setRdMapCollapsed(true);
    rdMapPanelRef.current?.collapse();
  }, [isMobile]);
  /** Буферная зона поиска пересечений (км); API получает метры. */
  const [intersectionBufferKm, setIntersectionBufferKm] = useState(3.5);
  const [geometryAnalysis, setGeometryAnalysis] = useState<{
    requestMapGeometryId: number;
    payload: GeometryAnalyzeResponse;
    toleranceMeters: number;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; label: string } | null>(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedForBulkDelete, setSelectedForBulkDelete] = useState<Set<number>>(new Set());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [geometryName, setGeometryName] = useState("");
  const [geometryId, setGeometryId] = useState("");
  /** Объект карты для выбора существующей геометрии по названию (Combobox). */
  const [rdGeometryObjectId, setRdGeometryObjectId] = useState("");
  const [geometryImportText, setGeometryImportText] = useState("");
  const [geometryCoordOrder, setGeometryCoordOrder] = useState<RdCoordinateOrder>("lonlat");
  const geometryImportFileRef = useRef<HTMLInputElement>(null);
  const [documentParseOpen, setDocumentParseOpen] = useState(false);
  const [generalForm, setGeneralForm] = useState<RdRequestPatchPayload>({
    request_source: "",
    request_doc_num: "",
    request_maker: "",
    request_maker_tel: "",
    request_content: "",
    kls_id_grif: undefined,
  });
  const [characteristicsForm, setCharacteristicsForm] = useState<RdRequestBowPatchPayload>({
    request_owner: "",
    request_goal: "",
    request_desc: "",
    kls_id_treq: undefined,
  });
  const [createForm, setCreateForm] = useState<RdRequestCreatePayload>({
    kls_id_type: 1,
    request_source: "",
    request_doc_num: "",
    request_doc_date: new Date().toISOString().slice(0, 10),
  });

  const requestsQuery = useQuery({
    queryKey: ["rd-requests", "list", searchTerm, requestTypeFilter],
    queryFn: () =>
      rdRequestsApi.list({
        limit: 100,
        request_source: searchTerm || undefined,
        kls_id_type: requestTypeFilter === "all" ? undefined : Number(requestTypeFilter),
      }),
  });
  const metaQuery = useQuery({
    queryKey: ["rd-requests", "meta"],
    queryFn: () => rdRequestsApi.getMeta(),
  });

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setSearchTerm((prev) => (prev === q ? prev : q));
    const type = searchParams.get("type") || "all";
    setRequestTypeFilter((prev) => (prev === type ? prev : type));
  }, [searchParams]);

  const requests = requestsQuery.data?.items ?? [];
  const visibleRequestIds = useMemo(() => requests.map((item) => item.request_id), [requests]);
  const allVisibleSelected =
    visibleRequestIds.length > 0 && visibleRequestIds.every((id) => selectedForBulkDelete.has(id));
  const someVisibleSelected = visibleRequestIds.some((id) => selectedForBulkDelete.has(id));
  const currentParentId = folderTrail[folderTrail.length - 1]?.id ?? null;
  const selectedRequest = useMemo(
    () => requests.find((x) => x.request_id === selectedRequestId) ?? null,
    [requests, selectedRequestId],
  );

  const detailsQuery = useQuery({
    queryKey: ["rd-requests", "detail", selectedRequestId],
    queryFn: () => rdRequestsApi.getById(selectedRequestId!),
    enabled: selectedRequestId != null,
  });

  const cardQuery = useQuery({
    queryKey: ["rd-requests", "card", selectedRequestId],
    queryFn: () => rdRequestsApi.getCard(selectedRequestId!),
    enabled: selectedRequestId != null,
  });

  const cardAuditQuery = useQuery({
    queryKey: ["rd-requests", "card-audit", selectedRequestId],
    queryFn: () => rdRequestsApi.listCardAudit(selectedRequestId!, { limit: 50 }),
    enabled: selectedRequestId != null && activeTab === "general",
  });

  const characteristicsQuery = useQuery({
    queryKey: ["rd-requests", "characteristics", selectedRequestId],
    queryFn: () => rdRequestsApi.getCharacteristics(selectedRequestId!),
    enabled: selectedRequestId != null,
  });

  const filestoreNodesQuery = useQuery({
    queryKey: ["rd-requests", "filestore", selectedRequestId, currentParentId],
    queryFn: () => rdRequestsApi.getFilestoreNodes(selectedRequestId!, currentParentId),
    enabled: selectedRequestId != null && activeTab === "materials",
  });

  const mapGeometriesQuery = useQuery({
    queryKey: ["rd-requests", "map-geometries", selectedRequestId],
    queryFn: () => rdRequestsApi.listMapGeometries(selectedRequestId!),
    enabled: selectedRequestId != null,
  });

  /** GO embed: reverse по первой точке drawn_geojson или fallback summary страны из ГО. */
  const goGeoContextQuery = useQuery({
    queryKey: ["go-embed", "rd", selectedRequestId, mapGeometriesQuery.data?.[0]?.request_map_geometry_id],
    queryFn: async () => {
      const first = mapGeometriesQuery.data?.[0] as
        | { drawn_geojson?: GeoJSON.Geometry | GeoJSON.Feature | null }
        | undefined;
      const drawn = first?.drawn_geojson;
      let lon: number | null = null;
      let lat: number | null = null;
      const geom =
        drawn && typeof drawn === "object" && "type" in drawn
          ? drawn.type === "Feature"
            ? (drawn as GeoJSON.Feature).geometry
            : (drawn as GeoJSON.Geometry)
          : null;
      if (geom) {
        const coords: number[] = [];
        const walk = (c: unknown): void => {
          if (!Array.isArray(c)) return;
          if (typeof c[0] === "number" && typeof c[1] === "number") {
            coords.push(c[0] as number, c[1] as number);
            return;
          }
          for (const x of c) walk(x);
        };
        walk((geom as GeoJSON.Geometry & { coordinates?: unknown }).coordinates);
        if (coords.length >= 2) {
          lon = coords[0];
          lat = coords[1];
        }
      }
      if (lon != null && lat != null) {
        const rev = await goEntitiesApi.reverseGeocode({
          longitude: lon,
          latitude: lat,
          include_geography: true,
        });
        return {
          kind: "reverse" as const,
          name: rev.country || rev.display_name || null,
          iso: null as string | null,
          facts: [
            ...(rev.state ? [{ label: "Регион", value: rev.state }] : []),
            ...(rev.city ? [{ label: "Город", value: rev.city }] : []),
          ],
        };
      }
      const list = await goEntitiesApi.list({ limit: 1, q: "Ангола" });
      const hit = list.items[0];
      if (!hit) return null;
      const sum = await goEntitiesApi.getSummary(hit.ginf_obj_id);
      return {
        kind: "go" as const,
        ginfObjId: hit.ginf_obj_id,
        name: sum.obj_name ?? hit.obj_name,
        iso: sum.iso_alpha3 ?? null,
        facts: [
          {
            label: "Тип",
            value: sum.entity_type_name || "—",
          },
        ],
      };
    },
    enabled: selectedRequestId != null && activeTab === "general",
    staleTime: 60_000,
  });

  const selectedRdGeometryObjectNum = useMemo(() => {
    const n = Number(rdGeometryObjectId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [rdGeometryObjectId]);

  const rdObjectsForGeometryPickerQuery = useQuery({
    queryKey: ["objects", "rd-geometry-picker"],
    queryFn: () => objectsApi.list({ limit: 1000 }),
    enabled: selectedRequestId != null && activeTab === "geometries",
    staleTime: 60_000,
  });

  const rdGeometriesForPickerQuery = useQuery({
    queryKey: ["objects", selectedRdGeometryObjectNum, "geometries", "rd-picker"],
    queryFn: () => objectsApi.getGeometries(selectedRdGeometryObjectNum!),
    enabled: selectedRdGeometryObjectNum != null && activeTab === "geometries",
    staleTime: 30_000,
  });

  const rdGeometryObjectOptions = useMemo<ComboboxOption[]>(() => {
    const rows = rdObjectsForGeometryPickerQuery.data ?? [];
    return rows.map((obj) => ({
      value: String(obj.id),
      label: `${obj.name || "Без названия"} (#${obj.id})`,
      shortLabel: obj.name || `#${obj.id}`,
      fullLabel: `${obj.name || "Без названия"} • ID: ${obj.id}`,
    }));
  }, [rdObjectsForGeometryPickerQuery.data]);

  const rdGeometryPickerOptions = useMemo<ComboboxOption[]>(() => {
    const rows = rdGeometriesForPickerQuery.data ?? [];
    return rows.map((g) => {
      const badge = geometryTypeBadge(g.type);
      return {
        value: String(g.id),
        label: `${g.name || g.type || "Геометрия"} (#${g.id})`,
        shortLabel: g.name || `${g.type || "Геометрия"} #${g.id}`,
        fullLabel: `${g.name || "Геометрия"} • Тип: ${g.type || "—"} • ID: ${g.id}`,
        badgeLabel: badge.label,
        badgeClassName: badge.className,
      };
    });
  }, [rdGeometriesForPickerQuery.data]);

  const patchRequestMutation = useMutation({
    mutationFn: async (payload: RdRequestPatchPayload) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.patch(selectedRequestId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "detail", selectedRequestId] });
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "card", selectedRequestId] });
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "list"] });
      notifyModuleSuccess(toast, "Общие сведения обновлены");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "обновление общих сведений РД", e);
    },
  });

  const updateCardMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.updateCard(selectedRequestId, { values });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "card", selectedRequestId] });
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "detail", selectedRequestId] });
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "characteristics", selectedRequestId] });
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "card-audit", selectedRequestId] });
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "list"] });
      notifyModuleSuccess(toast, "Карточка заявки сохранена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "сохранение карточки РД", e);
    },
  });
  const createRequestMutation = useMutation({
    mutationFn: async (payload: RdRequestCreatePayload) => rdRequestsApi.create(payload),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "list"] });
      setSelectedRequestId(created.request_id);
      setActiveTab("general");
      setCreateDialogOpen(false);
      setCreateForm((prev) => ({
        ...prev,
        request_source: "",
        request_doc_num: "",
        request_doc_date: new Date().toISOString().slice(0, 10),
      }));
      notifyModuleSuccess(toast, "Заявка РД создана");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "создание заявки РД", e);
    },
  });

  const deleteRequestMutation = useMutation({
    mutationFn: (requestId: number) => rdRequestsApi.deleteRequest(requestId),
    onSuccess: async (_, requestId) => {
      setDeleteConfirm(null);
      await queryClient.invalidateQueries({ queryKey: ["rd-requests"] });
      if (selectedRequestId === requestId) {
        setSelectedRequestId(null);
        setActiveTab("general");
      }
      notifyModuleSuccess(toast, "Заявка РД удалена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление заявки РД", e);
    },
  });
  const bulkDeleteRequestsMutation = useMutation({
    mutationFn: async (requestIds: number[]) => {
      const results = await Promise.allSettled(requestIds.map((id) => rdRequestsApi.deleteRequest(id)));
      const failed = results.filter((entry) => entry.status === "rejected").length;
      return { total: requestIds.length, failed };
    },
    onSuccess: async ({ total, failed }) => {
      setBulkDeleteConfirmOpen(false);
      setSelectedForBulkDelete(new Set());
      setBulkDeleteMode(false);
      await queryClient.invalidateQueries({ queryKey: ["rd-requests"] });
      if (selectedRequestId != null && selectedForBulkDelete.has(selectedRequestId)) {
        setSelectedRequestId(null);
        setActiveTab("general");
      }
      if (failed > 0) {
        notifyModuleError(
          toast,
          "массовое удаление заявок РД",
          new Error(`Удалено: ${total - failed}, ошибок: ${failed}`),
        );
        return;
      }
      notifyModuleSuccess(toast, `Удалено заявок: ${total}`);
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "массовое удаление заявок РД", e);
    },
  });

  const patchCharacteristicsMutation = useMutation({
    mutationFn: async (payload: RdRequestBowPatchPayload) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.patchCharacteristics(selectedRequestId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "characteristics", selectedRequestId] });
      notifyModuleSuccess(toast, "Характеристики обновлены");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "обновление характеристик РД", e);
    },
  });

  useEffect(() => {
    if (!detailsQuery.data) return;
    setGeneralForm({
      request_source: detailsQuery.data.request_source || "",
      request_doc_num: detailsQuery.data.request_doc_num || "",
      request_maker: detailsQuery.data.request_maker || "",
      request_maker_tel: detailsQuery.data.request_maker_tel || "",
      request_content: detailsQuery.data.request_content || "",
      kls_id_grif: detailsQuery.data.kls_id_grif ?? undefined,
    });
  }, [detailsQuery.data]);

  useEffect(() => {
    if (!characteristicsQuery.data) return;
    setCharacteristicsForm({
      request_owner: characteristicsQuery.data.request_owner || "",
      request_goal: characteristicsQuery.data.request_goal || "",
      request_desc: characteristicsQuery.data.request_desc || "",
      kls_id_treq: characteristicsQuery.data.kls_id_treq ?? undefined,
    });
  }, [characteristicsQuery.data]);

  useEffect(() => {
    setFolderTrail([{ id: null, name: "Корень" }]);
  }, [selectedRequestId]);

  useEffect(() => {
    setRdGeometryObjectId("");
    setGeometryId("");
  }, [selectedRequestId]);

  useEffect(() => {
    setGeometryId("");
  }, [rdGeometryObjectId]);

  useEffect(() => {
    if (!geometryId) return;
    if (!selectedRdGeometryObjectNum) return;
    const data = rdGeometriesForPickerQuery.data;
    if (!data) return;
    const exists = data.some((g) => String(g.id) === geometryId);
    if (!exists) setGeometryId("");
  }, [geometryId, selectedRdGeometryObjectNum, rdGeometriesForPickerQuery.data]);

  useEffect(() => {
    setSelectedForBulkDelete((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(requests.map((item) => item.request_id));
      const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [requests]);

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.uploadFile(selectedRequestId, file, {
        description: materialDescription,
        parentNodeId: currentParentId,
      });
    },
    onSuccess: async () => {
      setMaterialDescription("");
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "files", selectedRequestId] });
      await queryClient.invalidateQueries({
        queryKey: ["rd-requests", "filestore", selectedRequestId, currentParentId],
      });
      notifyModuleSuccess(toast, "Файл РД загружен");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "загрузка файла РД", e);
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (fileId: number) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.deleteFile(selectedRequestId, fileId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "files", selectedRequestId] });
      await queryClient.invalidateQueries({
        queryKey: ["rd-requests", "filestore", selectedRequestId, currentParentId],
      });
      notifyModuleSuccess(toast, "Файл РД удален");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление файла РД", e);
    },
  });
  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.createFilestoreFolder(selectedRequestId, {
        name,
        parent_id: currentParentId,
      });
    },
    onSuccess: async () => {
      setNewFolderOpen(false);
      setNewFolderName("");
      await queryClient.invalidateQueries({
        queryKey: ["rd-requests", "filestore", selectedRequestId, currentParentId],
      });
      notifyModuleSuccess(toast, "Папка создана");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "создание папки РД", e);
    },
  });

  const createMapGeometryMutation = useMutation({
    mutationFn: async (payload: RdMapGeometryCreatePayload) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.createMapGeometry(selectedRequestId, payload);
    },
    onSuccess: async () => {
      setGeometryName("");
      setGeometryId("");
      setGeometryImportText("");
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "map-geometries", selectedRequestId] });
      notifyModuleSuccess(toast, "Геометрия заявки добавлена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "добавление геометрии РД", e);
    },
  });

  const handleGeometryImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setGeometryImportText(String(reader.result ?? ""));
    reader.readAsText(file);
    e.target.value = "";
  };

  const submitGeometryImport = () => {
    const result = parseRdGeometryFromText(geometryImportText, { coordinateOrder: geometryCoordOrder });
    if (!result.ok) {
      notifyModuleError(toast, "импорт контура РД", new Error(result.error));
      return;
    }
    createMapGeometryMutation.mutate({
      map_geometry_name: geometryName || undefined,
      drawn_geojson: result.feature,
    });
  };

  const deleteMapGeometryMutation = useMutation({
    mutationFn: async (requestMapGeometryId: number) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      return rdRequestsApi.deleteMapGeometry(selectedRequestId, requestMapGeometryId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rd-requests", "map-geometries", selectedRequestId] });
      notifyModuleSuccess(toast, "Геометрия заявки удалена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление геометрии РД", e);
    },
  });

  const analyzeMapGeometryMutation = useMutation({
    mutationFn: async (requestMapGeometryId: number) => {
      if (!selectedRequestId) throw new Error("requestId is required");
      const km = Number(intersectionBufferKm);
      if (!Number.isFinite(km) || km < 0 || km > 50) {
        throw new Error("Буферная зона должна быть от 0 до 50 км");
      }
      const toleranceMeters = km * 1000;
      const payload = await rdRequestsApi.analyzeMapGeometry(selectedRequestId, requestMapGeometryId, {
        tolerance_meters: toleranceMeters,
      });
      return { payload, requestMapGeometryId, toleranceMeters };
    },
    onSuccess: ({ payload, requestMapGeometryId, toleranceMeters }) => {
      setGeometryAnalysis({ requestMapGeometryId, payload, toleranceMeters });
      notifyModuleSuccess(toast, `Найдено пересечений: ${payload.candidates.length}`);
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "пространственный анализ геометрии РД", e);
    },
  });

  useEffect(() => {
    return () => {
      if (previewImage?.url) {
        URL.revokeObjectURL(previewImage.url);
      }
    };
  }, [previewImage]);

  const uploadSelectedFile = (selected: File | undefined) => {
    if (!selected) return;
    uploadFileMutation.mutate(selected);
  };
  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedForBulkDelete((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const id of visibleRequestIds) next.add(id);
      } else {
        for (const id of visibleRequestIds) next.delete(id);
      }
      return next;
    });
  };

  const openImagePreview = async (
    nodeId: number,
    fileName: string,
    imageNodeIds: number[],
  ) => {
    if (!selectedRequestId) return;
    try {
      const res = await fetch(`/api/v1/rd-requests/${selectedRequestId}/files/${nodeId}/download?stream=true`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (previewImage?.url) {
        URL.revokeObjectURL(previewImage.url);
      }
      setPreviewImageIds(imageNodeIds);
      setPreviewImage({ nodeId, name: fileName, url });
      setPreviewOpen(true);
    } catch (error) {
      notifyModuleError(toast, "предпросмотр изображения РД", error);
    }
  };

  const navigatePreviewImage = async (direction: "prev" | "next") => {
    if (!selectedRequestId || !previewImage || previewImageIds.length < 2) return;
    const currentIndex = previewImageIds.indexOf(previewImage.nodeId);
    if (currentIndex < 0) return;
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % previewImageIds.length
        : (currentIndex - 1 + previewImageIds.length) % previewImageIds.length;
    const nextNodeId = previewImageIds[nextIndex];
    const nextNode = (filestoreNodesQuery.data ?? []).find((node) => node.id === nextNodeId);
    if (!nextNode) return;
    try {
      const res = await fetch(`/api/v1/rd-requests/${selectedRequestId}/files/${nextNodeId}/download?stream=true`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (previewImage?.url) {
        URL.revokeObjectURL(previewImage.url);
      }
      setPreviewImage({ nodeId: nextNodeId, name: nextNode.name, url });
    } catch (error) {
      notifyModuleError(toast, "переключение предпросмотра РД", error);
    }
  };

  return (
    <>
    <MainWorkspaceLayout
      header={
        <Header
          className="sticky top-0 z-50"
          onSearch={(query) => {
            setSearchTerm(query);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (query) next.set("q", query);
                else next.delete("q");
                return next;
              },
              { replace: true },
            );
          }}
        />
      }
      sidebar={
        <EntityListPanel
          title={
            <ModuleRegistryTitle title="Согласования" helpLabel="Справка по реестру согласований">
              <p className="font-medium">Зачем</p>
              <p>Реестр заявок РД (согласования): поиск и отбор записей для карточки справа.</p>
              <p className="font-medium mt-2">Как работать</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Поиск по источнику или № документа; фильтр по типу под строкой поиска</li>
                <li>«+» — диалог новой заявки; корзина — режим массового удаления</li>
                <li>Клик по строке открывает карточку; корзина у строки — удалить одну заявку</li>
              </ul>
            </ModuleRegistryTitle>
          }
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Поиск по источнику или № документа..."
          headerActions={
            bulkDeleteMode ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  disabled={bulkDeleteRequestsMutation.isPending}
                  onClick={() => {
                    setBulkDeleteMode(false);
                    setSelectedForBulkDelete(new Set());
                  }}
                >
                  Отменить
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="h-7 px-2 text-xs"
                  disabled={selectedForBulkDelete.size === 0 || bulkDeleteRequestsMutation.isPending}
                  onClick={() => setBulkDeleteConfirmOpen(true)}
                  title="Удалить выбранные"
                >
                  Удалить ({selectedForBulkDelete.size})
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  title="Массовое удаление"
                  aria-label="Массовое удаление"
                  disabled={bulkDeleteRequestsMutation.isPending}
                  onClick={() => {
                    setBulkDeleteMode(true);
                    setSelectedForBulkDelete(new Set());
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  className="h-7 w-7"
                  title="Добавить заявку"
                  aria-label="Добавить заявку"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </>
            )
          }
          headerExtras={
            <div className="space-y-2">
              <Select
                value={requestTypeFilter}
                onValueChange={(value) => {
                  setRequestTypeFilter(value);
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      if (value === "all") next.delete("type");
                      else next.set("type", value);
                      return next;
                    },
                    { replace: true },
                  );
                }}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Фильтр по типу запроса" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="all">Все типы</SelectItem>
                  {(metaQuery.data?.request_types ?? []).map((item) => (
                    <SelectItem key={item.kls_id} value={String(item.kls_id)}>
                      {item.kls_names}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bulkDeleteMode ? (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2 rounded-md border px-2 py-1">
                    <Checkbox
                      checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                      onCheckedChange={(checked) => toggleSelectAllVisible(checked === true)}
                      aria-label="Выбрать все заявки в текущем списке"
                      disabled={requests.length === 0 || bulkDeleteRequestsMutation.isPending}
                    />
                    <span className="text-xs text-muted-foreground">Выбрать все</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={selectedForBulkDelete.size === 0 || bulkDeleteRequestsMutation.isPending}
                    onClick={() => setSelectedForBulkDelete(new Set())}
                  >
                    Сбросить
                  </Button>
                </div>
              ) : null}
            </div>
          }
          isLoading={requestsQuery.isLoading}
          isError={requestsQuery.isError}
          errorText="Ошибка загрузки реестра РД. Проверьте доступ и API."
          emptyText="Реестр РД пуст: записи не найдены или недоступны по фильтру."
          items={requests}
          selectedId={selectedRequestId}
          getId={(item) => item.request_id}
          onSelect={setSelectedRequestId}
          renderItem={(item) => (
            <>
              <div className="text-sm font-medium truncate">{item.request_type_name || `Тип #${item.kls_id_type}`}</div>
              <div className="text-xs text-muted-foreground mt-1 truncate">Источник: {item.request_source}</div>
              <div className="text-xs text-muted-foreground">
                № {item.request_doc_num} от {formatDate(item.request_doc_date)}
              </div>
            </>
          )}
          renderSideActions={(item) => (
            bulkDeleteMode ? (
              <Checkbox
                checked={selectedForBulkDelete.has(item.request_id)}
                onCheckedChange={(checked) =>
                  setSelectedForBulkDelete((prev) => {
                    const next = new Set(prev);
                    if (checked === true) next.add(item.request_id);
                    else next.delete(item.request_id);
                    return next;
                  })
                }
                aria-label={`Выбрать заявку #${item.request_id}`}
                className="mr-2"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                title="Удалить заявку"
                aria-label="Удалить заявку"
                onClick={(e) => {
                  e.stopPropagation();
                  const label =
                    item.request_type_name?.trim() ||
                    `№ ${item.request_doc_num} от ${formatDate(item.request_doc_date)}`;
                  setDeleteConfirm({ id: item.request_id, label });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )
          )}
        />
      }
      workspace={
        <div className="absolute inset-0 min-h-0 bg-background">
          <ResizablePanelGroup
            direction="horizontal"
            autoSaveId="rd-workspace-oi-map-split"
            className="h-full w-full"
          >
            <ResizablePanel defaultSize={60} minSize={28} className="min-h-0 min-w-0">
          <div className="h-full min-h-0 overflow-auto p-4">
          <EntityDetailsShell
            selected={selectedRequestId != null}
            selectedTitle={selectedRequest?.request_type_name || (selectedRequestId != null ? `#${selectedRequestId}` : undefined)}
            emptyTitle="Выберите заявку РД"
            emptyDescription="Слева доступен реестр. Откройте запись, чтобы перейти в карточку с разделами «Общие сведения / Характеристики / Материалы»."
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={[
              {
                value: "general",
                label: "Общие сведения",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Общие сведения</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {detailsQuery.isLoading ? (
                        <Skeleton className="h-20 w-full" />
                      ) : detailsQuery.data ? (
                        <>
                          <GeoContextPanel
                            title="Географический контекст (ГО)"
                            ginfObjId={
                              goGeoContextQuery.data?.kind === "go"
                                ? goGeoContextQuery.data.ginfObjId
                                : null
                            }
                            name={goGeoContextQuery.data?.name}
                            iso={goGeoContextQuery.data?.iso}
                            facts={goGeoContextQuery.data?.facts}
                            emptyHint={
                              goGeoContextQuery.isLoading
                                ? "Загрузка geo-context…"
                                : "Нет контура заявки и сущностей ГО — откройте модуль ГО"
                            }
                            onOpenInGo={(id) => navigate(`/go?entityId=${id}`)}
                          />
                          {cardQuery.isLoading ? (
                            <Skeleton className="h-40 w-full" />
                          ) : cardQuery.data?.template_resolved && cardQuery.data.template ? (
                            <div className="space-y-4">
                              {cardQuery.data.message ? (
                                <p className="text-sm text-muted-foreground">{cardQuery.data.message}</p>
                              ) : null}
                              <RdDynamicCardRenderer
                                card={cardQuery.data}
                                meta={metaQuery.data}
                                saving={updateCardMutation.isPending}
                                onSave={(values) => updateCardMutation.mutate(values)}
                              />
                              <Collapsible className="rounded-md border">
                                <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/50 [&[data-state=open]>svg]:rotate-180">
                                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                  Журнал карточки (шаблон РД)
                                </CollapsibleTrigger>
                                <CollapsibleContent className="px-3 pb-3">
                                  {cardAuditQuery.isLoading ? (
                                    <Skeleton className="h-16 w-full" />
                                  ) : (cardAuditQuery.data?.length ?? 0) === 0 ? (
                                    <p className="text-xs text-muted-foreground">Записей пока нет</p>
                                  ) : (
                                    <ul className="space-y-2 text-xs">
                                      {(cardAuditQuery.data ?? []).map((row) => (
                                        <li key={row.id} className="rounded border bg-muted/30 p-2">
                                          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-muted-foreground">
                                            <span>{formatDate(row.created_at)}</span>
                                            <span>
                                              {row.user_email ?? row.user_username ?? row.user_id ?? "—"}
                                            </span>
                                          </div>
                                          <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-snug">
                                            {JSON.stringify(row.payload ?? {}, null, 2)}
                                          </pre>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          ) : (
                            <>
                              {cardQuery.data?.message ? (
                                <Alert className="mb-2">
                                  <AlertTitle className="text-sm">Карточка по шаблону</AlertTitle>
                                  <AlertDescription className="text-xs">{cardQuery.data.message}</AlertDescription>
                                </Alert>
                              ) : null}
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <Label>Откуда поступил</Label>
                                  <Input
                                    value={generalForm.request_source || ""}
                                    onChange={(e) =>
                                      setGeneralForm((prev) => ({ ...prev, request_source: e.target.value }))
                                    }
                                  />
                                </div>
                                <div>
                                  <Label>№ исходящего</Label>
                                  <Input
                                    value={generalForm.request_doc_num || ""}
                                    onChange={(e) =>
                                      setGeneralForm((prev) => ({ ...prev, request_doc_num: e.target.value }))
                                    }
                                  />
                                </div>
                                <div>
                                  <Label>Гриф секретности</Label>
                                  <Select
                                    value={generalForm.kls_id_grif != null ? String(generalForm.kls_id_grif) : "none"}
                                    onValueChange={(value) =>
                                      setGeneralForm((prev) => ({
                                        ...prev,
                                        kls_id_grif: value === "none" ? undefined : Number(value),
                                      }))
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Выберите гриф" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[250]">
                                      <SelectItem value="none">Не задан</SelectItem>
                                      {(metaQuery.data?.secrecy_grifs ?? []).map((item) => (
                                        <SelectItem key={item.kls_id} value={String(item.kls_id)}>
                                          {item.kls_names}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Контактное лицо</Label>
                                  <Input
                                    value={generalForm.request_maker || ""}
                                    onChange={(e) =>
                                      setGeneralForm((prev) => ({ ...prev, request_maker: e.target.value }))
                                    }
                                  />
                                </div>
                                <div>
                                  <Label>Телефон</Label>
                                  <Input
                                    value={generalForm.request_maker_tel || ""}
                                    onChange={(e) =>
                                      setGeneralForm((prev) => ({ ...prev, request_maker_tel: e.target.value }))
                                    }
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Содержание документа</Label>
                                <Input
                                  value={generalForm.request_content || ""}
                                  onChange={(e) =>
                                    setGeneralForm((prev) => ({ ...prev, request_content: e.target.value }))
                                  }
                                />
                              </div>
                              <Button
                                onClick={() => patchRequestMutation.mutate(generalForm)}
                                disabled={patchRequestMutation.isPending}
                              >
                                {patchRequestMutation.isPending ? "Сохранение..." : "Сохранить"}
                              </Button>
                            </>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-muted-foreground">Данные не найдены</div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "characteristics",
                label: "Характеристики",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Характеристики</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {characteristicsQuery.isLoading ? (
                        <Skeleton className="h-20 w-full" />
                      ) : (
                        <>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <Label>Владелец</Label>
                              <Input
                                value={characteristicsForm.request_owner || ""}
                                onChange={(e) =>
                                  setCharacteristicsForm((prev) => ({ ...prev, request_owner: e.target.value }))
                                }
                              />
                            </div>
                            <div>
                              <Label>Тип запроса территории</Label>
                              <Select
                                value={characteristicsForm.kls_id_treq != null ? String(characteristicsForm.kls_id_treq) : "none"}
                                onValueChange={(value) =>
                                  setCharacteristicsForm((prev) => ({
                                    ...prev,
                                    kls_id_treq: value === "none" ? undefined : Number(value),
                                  }))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Выберите тип" />
                                </SelectTrigger>
                                <SelectContent className="z-[250]">
                                  <SelectItem value="none">Не задан</SelectItem>
                                  {(metaQuery.data?.territory_request_types ?? []).map((item) => (
                                    <SelectItem key={item.kls_id} value={String(item.kls_id)}>
                                      {item.kls_names}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Цель</Label>
                              <Input
                                value={characteristicsForm.request_goal || ""}
                                onChange={(e) =>
                                  setCharacteristicsForm((prev) => ({ ...prev, request_goal: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Примечание</Label>
                            <Input
                              value={characteristicsForm.request_desc || ""}
                              onChange={(e) =>
                                setCharacteristicsForm((prev) => ({ ...prev, request_desc: e.target.value }))
                              }
                            />
                          </div>
                          <Button
                            onClick={() => patchCharacteristicsMutation.mutate(characteristicsForm)}
                            disabled={patchCharacteristicsMutation.isPending}
                          >
                            {patchCharacteristicsMutation.isPending ? "Сохранение..." : "Сохранить"}
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "geometries",
                label: "Геометрии",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-1.5 text-base">
                        <span>Геометрии территории запроса</span>
                        <SectionHelpTooltip label="Как добавить контур территории" side="bottom">
                          <p className="font-medium">Как добавить контур территории</p>
                          <p>
                            Контур сохраняется как GeoJSON (WGS84, EPSG:4326). Можно комбинировать способы:
                          </p>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>
                              <strong>GeoJSON</strong> — Feature / Geometry / FeatureCollection (берётся первый
                              объект).
                            </li>
                            <li>
                              <strong>Координаты</strong> — по одной точке на строку; по умолчанию долгота, широта
                              (порядок можно сменить).
                            </li>
                            <li>
                              <strong>Файл</strong> — .geojson / .json / текст с тем же содержимым.
                            </li>
                            <li>
                              <strong>Карта</strong> — «Нарисовать на карте» справа.
                            </li>
                          </ul>
                        </SectionHelpTooltip>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Collapsible className="rounded-md border border-border bg-muted/30 px-3 py-2">
                        <CollapsibleTrigger asChild>
                          <Button type="button" variant="ghost" className="flex h-auto w-full justify-between gap-2 py-2 px-0 hover:bg-transparent">
                            <span className="text-left font-medium">Импорт контура: координаты или файл</span>
                            <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-4 pb-2 pt-1">
                          <input
                            ref={geometryImportFileRef}
                            type="file"
                            accept=".geojson,.json,application/geo+json,text/plain"
                            className="hidden"
                            onChange={handleGeometryImportFileChange}
                          />

                          <div className="space-y-1.5 max-w-md">
                            <Label htmlFor="rd-geometry-coord-order">Порядок координат</Label>
                            <Select
                              value={geometryCoordOrder}
                              onValueChange={(v) => setGeometryCoordOrder(v as RdCoordinateOrder)}
                            >
                              <SelectTrigger id="rd-geometry-coord-order" className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="z-[250]">
                                <SelectItem value="lonlat">Долгота, широта (как в GeoJSON)</SelectItem>
                                <SelectItem value="latlon">Широта, долгота</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Только для текстового списка; на GeoJSON не влияет.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="rd-geometry-import-text">Текст GeoJSON или список координат</Label>
                            <Textarea
                              id="rd-geometry-import-text"
                              rows={8}
                              placeholder={`Пример GeoJSON:\n{"type":"Polygon","coordinates":[[[37.6,55.7],[37.7,55.7],[37.7,55.8],[37.6,55.8],[37.6,55.7]]]}\n\nПример координат (долгота широта):\n37.61 55.75\n37.62 55.76\n37.63 55.75`}
                              value={geometryImportText}
                              onChange={(e) => setGeometryImportText(e.target.value)}
                              className="font-mono text-xs"
                            />
                          </div>

                          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  downloadGeometryCoordsTemplate(
                                    "geopulseml-geometry-template.geojson",
                                    GEOMETRY_COORDS_GEOJSON_TEMPLATE,
                                    "application/geo+json;charset=utf-8",
                                  )
                                }
                              >
                                <Download className="mr-1.5 h-4 w-4 shrink-0" />
                                Шаблон GeoJSON
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const content =
                                    geometryCoordOrder === "latlon"
                                      ? GEOMETRY_COORDS_TXT_TEMPLATE_LATLON
                                      : GEOMETRY_COORDS_TXT_TEMPLATE_LONLAT;
                                  const suffix = geometryCoordOrder === "latlon" ? "latlon" : "lonlat";
                                  downloadGeometryCoordsTemplate(
                                    `geopulseml-coords-template-${suffix}.txt`,
                                    content,
                                  );
                                }}
                              >
                                <Download className="mr-1.5 h-4 w-4 shrink-0" />
                                Шаблон координат
                              </Button>
                            </div>
                            <div className="hidden h-6 w-px bg-border sm:block" aria-hidden />
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => geometryImportFileRef.current?.click()}
                              >
                                <Upload className="mr-1.5 h-4 w-4 shrink-0" />
                                Загрузить файл
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setDocumentParseOpen(true)}
                              >
                                <ScanSearch className="mr-1.5 h-4 w-4 shrink-0" />
                                Распознать из документа
                              </Button>
                            </div>
                            <div className="sm:ml-auto">
                              <Button
                                type="button"
                                size="sm"
                                className="w-full sm:w-auto"
                                disabled={createMapGeometryMutation.isPending || !geometryImportText.trim()}
                                onClick={submitGeometryImport}
                              >
                                {createMapGeometryMutation.isPending ? "Добавление..." : "Добавить контур"}
                              </Button>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Объект на карте</Label>
                          <Combobox
                            options={rdGeometryObjectOptions}
                            value={rdGeometryObjectId || undefined}
                            onValueChange={(value) => setRdGeometryObjectId(value ?? "")}
                            placeholder={
                              rdObjectsForGeometryPickerQuery.isLoading
                                ? "Загрузка объектов..."
                                : "Выберите объект"
                            }
                            searchPlaceholder="Поиск объекта..."
                            emptyText={
                              rdObjectsForGeometryPickerQuery.isLoading ? "Загрузка..." : "Объекты не найдены"
                            }
                            disabled={rdObjectsForGeometryPickerQuery.isLoading}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Существующая геометрия</Label>
                          <Combobox
                            options={rdGeometryPickerOptions}
                            value={geometryId || undefined}
                            onValueChange={(value) => {
                              setGeometryId(value ?? "");
                              const row = rdGeometriesForPickerQuery.data?.find((g) => String(g.id) === value);
                              if (row?.name) {
                                setGeometryName((prev) => (prev.trim() ? prev : row.name || ""));
                              }
                            }}
                            placeholder={
                              !selectedRdGeometryObjectNum
                                ? "Сначала выберите объект"
                                : rdGeometriesForPickerQuery.isLoading
                                  ? "Загрузка геометрий..."
                                  : "Выберите геометрию по названию"
                            }
                            searchPlaceholder="Поиск по названию геометрии..."
                            emptyText={
                              !selectedRdGeometryObjectNum
                                ? "Выберите объект слева"
                                : rdGeometriesForPickerQuery.isLoading
                                  ? "Загрузка..."
                                  : "Геометрии не найдены"
                            }
                            disabled={!selectedRdGeometryObjectNum || rdGeometriesForPickerQuery.isLoading}
                          />
                        </div>
                      </div>
                      <div className="grid gap-2 md:grid-cols-[1fr_auto_auto]">
                        <Input
                          placeholder="Название геометрии (опционально)"
                          value={geometryName}
                          onChange={(e) => setGeometryName(e.target.value)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setRdMapDrawing(true);
                            notifyModuleSuccess(toast, "Режим рисования на карте справа");
                          }}
                        >
                          <Crosshair className="mr-1 h-4 w-4" />
                          Нарисовать на карте
                        </Button>
                        <Button
                          type="button"
                          disabled={createMapGeometryMutation.isPending}
                          onClick={() =>
                            createMapGeometryMutation.mutate({
                              map_geometry_name: geometryName || undefined,
                              map_geometry_id: geometryId ? Number(geometryId) : null,
                            })
                          }
                        >
                          Добавить привязку
                        </Button>
                      </div>

                      {(mapGeometriesQuery.data ?? []).length > 0 ? (
                        <div className="flex flex-wrap items-end gap-3 rounded-md border border-border/70 bg-muted/20 px-2.5 py-2">
                          <div className="grid gap-1">
                            <Label htmlFor="rd-intersection-buffer-km" className="text-xs text-muted-foreground">
                              Буфер, км
                            </Label>
                            <Input
                              id="rd-intersection-buffer-km"
                              type="number"
                              min={0}
                              max={50}
                              step={0.1}
                              className="h-8 w-28"
                              value={intersectionBufferKm}
                              onChange={(e) => setIntersectionBufferKm(Number(e.target.value))}
                              title="Буферная зона поиска пересечений (по умолчанию 3.5 км)"
                            />
                          </div>
                          <div className="pb-1 text-xs text-muted-foreground">
                            По умолчанию 3.5 км · диапазон 0–50
                          </div>
                        </div>
                      ) : null}

                      {mapGeometriesQuery.isLoading ? (
                        <div className="text-sm text-muted-foreground">Загрузка списка геометрий...</div>
                      ) : (mapGeometriesQuery.data ?? []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Геометрии заявки пока не добавлены</div>
                      ) : (
                        <div className="space-y-2">
                          {(mapGeometriesQuery.data ?? []).map((item) => (
                            <div
                              key={item.request_map_geometry_id}
                              className="rounded-md border border-border p-2.5"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium">
                                    {item.map_geometry_name || `Геометрия #${item.request_map_geometry_id}`}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {item.drawn_geojson
                                      ? "Источник: нарисованный контур в РД"
                                      : `map_geometry_id: ${item.map_geometry_id ?? "не указан"}`}
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center justify-end gap-1">
                                  {geometryAnalysis?.requestMapGeometryId === item.request_map_geometry_id &&
                                  geometryAnalysis.payload.candidates.length > 0 ? (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      type="button"
                                      title="Печать отчёта о пересечениях (PDF)"
                                      onClick={() => {
                                        try {
                                          const detail = detailsQuery.data;
                                          printRdIntersectionReport({
                                            requestId: selectedRequestId!,
                                            requestTypeName:
                                              selectedRequest?.request_type_name ?? null,
                                            requestSource:
                                              detail?.request_source ??
                                              selectedRequest?.request_source ??
                                              null,
                                            requestDocNum:
                                              detail?.request_doc_num ??
                                              selectedRequest?.request_doc_num ??
                                              null,
                                            requestDocDate:
                                              detail?.request_doc_date ??
                                              selectedRequest?.request_doc_date ??
                                              null,
                                            requestMaker:
                                              detail?.request_maker ??
                                              selectedRequest?.request_maker ??
                                              null,
                                            geometryName:
                                              item.map_geometry_name?.trim() ||
                                              `Геометрия #${item.request_map_geometry_id}`,
                                            requestMapGeometryId: item.request_map_geometry_id,
                                            geometrySourceLabel: item.drawn_geojson
                                              ? "Нарисованный контур в РД"
                                              : item.map_geometry_id
                                                ? `Привязка к геометрии ОИ #${item.map_geometry_id}`
                                                : "Не указан",
                                            toleranceMeters: geometryAnalysis.toleranceMeters,
                                            analysis: geometryAnalysis.payload,
                                          });
                                        } catch (error) {
                                          notifyModuleError(toast, "печать отчёта о пересечениях", error);
                                        }
                                      }}
                                    >
                                      <Printer className="h-4 w-4" />
                                    </Button>
                                  ) : null}
                                  {item.drawn_geojson ? (
                                    <>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        title="Показать контур на карте РД"
                                        type="button"
                                        onClick={() => {
                                          const gj = item.drawn_geojson;
                                          if (!gj) return;
                                          setMapPreviewGeometry(
                                            geometryHighlightFromRdDrawn(
                                              item.request_map_geometry_id,
                                              item.map_geometry_name,
                                              gj,
                                            ),
                                          );
                                          setMapPreviewNonce((n) => n + 1);
                                        }}
                                      >
                                        <Map className="h-4 w-4" />
                                      </Button>
                                    </>
                                  ) : null}
                                  {!item.drawn_geojson && item.map_geometry_id ? (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      title="Показать контур на карте РД"
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          const geometry = await rdRequestsApi.getGeometryById(item.map_geometry_id!);
                                          setMapPreviewGeometry(geometry);
                                          setMapPreviewNonce((n) => n + 1);
                                        } catch (error) {
                                          notifyModuleError(toast, "загрузка геометрии для просмотра", error);
                                        }
                                      }}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  ) : null}
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    type="button"
                                    disabled={analyzeMapGeometryMutation.isPending}
                                    title="Поиск пересечений с объектами"
                                    onClick={() => analyzeMapGeometryMutation.mutate(item.request_map_geometry_id)}
                                  >
                                    <ScanSearch className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    type="button"
                                    disabled={deleteMapGeometryMutation.isPending}
                                    title="Удалить"
                                    onClick={() => deleteMapGeometryMutation.mutate(item.request_map_geometry_id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              {geometryAnalysis?.requestMapGeometryId === item.request_map_geometry_id && (
                                <div className="mt-2 rounded-md bg-muted/40 p-2 text-xs">
                                  <div className="mb-1 font-medium">
                                    Найдено пересечений: {geometryAnalysis.payload.candidates.length}
                                    <span className="ml-2 font-normal text-muted-foreground">
                                      (буфер {(geometryAnalysis.toleranceMeters / 1000).toFixed(1)} км)
                                    </span>
                                  </div>
                                  <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                                    {geometryAnalysis.payload.candidates.map((candidate) => (
                                      <div
                                        key={`${candidate.object_id}-${candidate.geometry_id}`}
                                        className="flex items-center justify-between gap-2"
                                      >
                                        <span className="min-w-0 truncate">
                                          {candidate.object_name} · {candidate.relation}
                                        </span>
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          className="h-7 shrink-0 px-2 text-[11px]"
                                          title="Показать пересечение на карте РД"
                                          onClick={async () => {
                                            try {
                                              const geometry = await rdRequestsApi.getGeometryById(
                                                candidate.geometry_id,
                                              );
                                              setMapPreviewGeometry(geometry);
                                              setMapPreviewNonce((n) => n + 1);
                                            } catch (error) {
                                              notifyModuleError(
                                                toast,
                                                "загрузка геометрии пересечения",
                                                error,
                                              );
                                            }
                                          }}
                                        >
                                          На карте
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "materials",
                label: "Материалы для согласования",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Документы и материалы для согласования</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex min-h-7 flex-wrap items-center gap-0.5 text-xs text-muted-foreground">
                        {folderTrail.map((crumb, index) => (
                          <div key={`${index}-${String(crumb.id)}`} className="flex min-w-0 max-w-full items-center">
                            {index > 0 && <ChevronRight className="mx-0.5 h-3.5 w-3.5 shrink-0" />}
                            <Button
                              type="button"
                              variant="link"
                              className="h-auto min-w-0 max-w-[11rem] truncate p-0 px-0.5 text-xs font-normal text-foreground"
                              onClick={() => setFolderTrail((prev) => prev.slice(0, index + 1))}
                            >
                              {crumb.name}
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => {
                            setNewFolderName("");
                            setNewFolderOpen(true);
                          }}
                        >
                          <FolderPlus className="mr-1.5 h-3.5 w-3.5" />
                          Новая папка
                        </Button>
                      </div>
                      <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                        <Input
                          placeholder="Описание файла (опционально)"
                          value={materialDescription}
                          onChange={(e) => setMaterialDescription(e.target.value)}
                        />
                        <div className="relative">
                          <Button type="button" disabled={uploadFileMutation.isPending}>
                            <Upload className="mr-1 h-4 w-4" />
                            {uploadFileMutation.isPending ? "Загрузка..." : "Загрузить файл"}
                          </Button>
                          <input
                            type="file"
                            className="absolute inset-0 cursor-pointer opacity-0"
                            onChange={(e) => {
                              uploadSelectedFile(e.target.files?.[0]);
                              e.currentTarget.value = "";
                            }}
                          />
                        </div>
                      </div>
                      <div
                        className={`cursor-pointer rounded-md border-2 border-dashed p-3 text-center text-xs transition-colors ${
                          isDragActive
                            ? "border-primary/60 bg-primary/10 dark:bg-primary/15"
                            : "border-border hover:border-muted-foreground/40"
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragActive(true);
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          setIsDragActive(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDragActive(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDragActive(false);
                          uploadSelectedFile(e.dataTransfer.files?.[0]);
                        }}
                      >
                        {isDragActive ? "Отпустите файл для загрузки" : "Перетащите файл сюда для загрузки в текущую папку"}
                      </div>
                      {filestoreNodesQuery.isLoading ? (
                        <div className="text-sm text-muted-foreground">Загрузка содержимого папки...</div>
                      ) : (filestoreNodesQuery.data ?? []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">В этой папке пока пусто</div>
                      ) : (
                        <div className="space-y-2">
                          {(filestoreNodesQuery.data ?? []).map((node) =>
                            node.kind === "folder" ? (
                              <button
                                key={node.id}
                                type="button"
                                onClick={() => setFolderTrail((prev) => [...prev, { id: node.id, name: node.name }])}
                                className="flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border p-2.5 text-left transition-colors hover:bg-muted/60"
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                  <Folder className="h-4 w-4 shrink-0 text-amber-600/90 dark:text-amber-500/90" />
                                  <span className="truncate text-sm font-medium text-foreground">{node.name}</span>
                                </div>
                                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              </button>
                            ) : (
                              <div
                                key={node.id}
                                className="grid grid-cols-[1fr_auto] items-center gap-2 rounded-md border border-border p-2"
                              >
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium text-foreground">{node.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {formatFileSize(node.size ?? 0)} · {node.mimetype ?? "application/octet-stream"}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {(node.mimetype ?? "").toLowerCase().startsWith("image/") && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() =>
                                        openImagePreview(
                                          node.id,
                                          node.name,
                                          (filestoreNodesQuery.data ?? [])
                                            .filter(
                                              (candidate) =>
                                                candidate.kind === "file" &&
                                                (candidate.mimetype ?? "").toLowerCase().startsWith("image/"),
                                            )
                                            .map((candidate) => candidate.id),
                                        )
                                      }
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={async () => {
                                      if (!selectedRequestId) return;
                                      const res = await fetch(
                                        `/api/v1/rd-requests/${selectedRequestId}/files/${node.id}/download`,
                                        {
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
                                          },
                                        },
                                      );
                                      const contentType = res.headers.get("content-type") || "";
                                      if (contentType.includes("application/json")) {
                                        const payload = await res.json();
                                        window.open(payload.url, "_blank", "noopener,noreferrer");
                                        return;
                                      }
                                      const blob = await res.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement("a");
                                      a.href = url;
                                      a.download = node.name;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                    }}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    disabled={deleteFileMutation.isPending}
                                    onClick={() => deleteFileMutation.mutate(node.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
            ]}
          />
          <Dialog
            open={newFolderOpen}
            onOpenChange={(open) => {
              setNewFolderOpen(open);
              if (!open) setNewFolderName("");
            }}
          >
            <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Новая папка</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-1">
                <Label htmlFor="rd-new-folder-name">Название</Label>
                <Input
                  id="rd-new-folder-name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Например, Документы"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newFolderName.trim() && !createFolderMutation.isPending) {
                      e.preventDefault();
                      createFolderMutation.mutate(newFolderName.trim());
                    }
                  }}
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setNewFolderOpen(false);
                    setNewFolderName("");
                  }}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  disabled={!newFolderName.trim() || createFolderMutation.isPending}
                  onClick={() => createFolderMutation.mutate(newFolderName.trim())}
                >
                  {createFolderMutation.isPending ? "Создание..." : "Создать"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog
            open={previewOpen}
            onOpenChange={(open) => {
              setPreviewOpen(open);
              if (!open && previewImage?.url) {
                URL.revokeObjectURL(previewImage.url);
                setPreviewImage(null);
              }
            }}
          >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="truncate pr-8">{previewImage?.name ?? "Предпросмотр изображения"}</DialogTitle>
              </DialogHeader>
              <div className="relative flex-1 min-h-0 overflow-auto rounded-md bg-muted/30">
                {previewImage ? (
                  <>
                    <img src={previewImage.url} alt={previewImage.name} className="mx-auto max-h-[70vh] object-contain" />
                    {previewImageIds.length > 1 && (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="absolute left-2 top-1/2 -translate-y-1/2"
                          onClick={() => void navigatePreviewImage("prev")}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() => void navigatePreviewImage("next")}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="p-4 text-sm text-muted-foreground">Изображение недоступно</div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          </div>
            </ResizablePanel>
            <ResizableHandle withHandle className="w-1.5 bg-border hover:bg-primary/30" />
            <ResizablePanel
              ref={rdMapPanelRef}
              defaultSize={40}
              minSize={18}
              collapsible
              collapsedSize={4}
              onCollapse={() => setRdMapCollapsed(true)}
              onExpand={() => setRdMapCollapsed(false)}
              className="min-h-0 min-w-0"
            >
          <RdWorkspaceMapPanel
            collapsed={rdMapCollapsed}
            onRequestCollapse={() => rdMapPanelRef.current?.collapse()}
            onRequestExpand={() => rdMapPanelRef.current?.expand()}
            requestMapGeometries={mapGeometriesQuery.data ?? []}
            onOiObjectSelect={(objectId) => {
              setRdGeometryObjectId(String(objectId));
              setActiveTab("geometries");
              notifyModuleSuccess(toast, `Выбран объект ОИ #${objectId} для привязки`);
            }}
            drawingActive={rdMapDrawing}
            onDrawingActiveChange={setRdMapDrawing}
            savePending={createMapGeometryMutation.isPending}
            onSaveDrawn={({ name, geojson }) => {
              createMapGeometryMutation.mutate(
                {
                  map_geometry_name: name || undefined,
                  drawn_geojson: geojson,
                },
                {
                  onSuccess: () => {
                    setGeometryName(name);
                    setRdMapDrawing(false);
                  },
                },
              );
            }}
            previewGeometry={mapPreviewGeometry}
            previewNonce={mapPreviewNonce}
            onPreviewConsumed={() => setMapPreviewGeometry(null)}
          />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      }
    />
    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
      <DialogContent className="sm:max-w-md z-[220]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Новая заявка РД</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="rd-create-source">Источник</Label>
            <Input
              id="rd-create-source"
              className="h-8"
              placeholder="Источник"
              value={createForm.request_source}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  request_source: e.target.value,
                }))
              }
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="rd-create-doc-num">№ документа</Label>
            <Input
              id="rd-create-doc-num"
              className="h-8"
              placeholder="№ документа"
              value={createForm.request_doc_num}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  request_doc_num: e.target.value,
                }))
              }
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="rd-create-doc-date">Дата документа</Label>
            <Input
              id="rd-create-doc-date"
              className="h-8"
              type="date"
              value={createForm.request_doc_date}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  request_doc_date: e.target.value,
                }))
              }
            />
          </div>
          <div className="grid gap-1">
            <Label>Тип запроса</Label>
            <Select
              value={String(createForm.kls_id_type ?? "")}
              onValueChange={(value) =>
                setCreateForm((prev) => ({
                  ...prev,
                  kls_id_type: Number(value),
                }))
              }
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Тип запроса" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                {(metaQuery.data?.request_types ?? []).map((item) => (
                  <SelectItem key={item.kls_id} value={String(item.kls_id)}>
                    {item.kls_names}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCreateDialogOpen(false)}
            disabled={createRequestMutation.isPending}
          >
            Отмена
          </Button>
          <Button
            type="button"
            disabled={
              createRequestMutation.isPending ||
              !createForm.request_source.trim() ||
              !createForm.request_doc_num.trim() ||
              !createForm.request_doc_date ||
              !createForm.kls_id_type
            }
            onClick={() => createRequestMutation.mutate(createForm)}
          >
            {createRequestMutation.isPending ? "Создание..." : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog
      open={deleteConfirm != null}
      onOpenChange={(open) => {
        if (!open) setDeleteConfirm(null);
      }}
    >
      <AlertDialogContent className="z-[230]">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить заявку РД?</AlertDialogTitle>
          <AlertDialogDescription>
            Запись «{deleteConfirm?.label ?? ""}» будет помечена как удалённая вместе с привязанными материалами и
            контурами. Восстановление через этот интерфейс недоступно.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteRequestMutation.isPending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={deleteRequestMutation.isPending || deleteConfirm == null}
            onClick={(e) => {
              e.preventDefault();
              if (deleteConfirm) deleteRequestMutation.mutate(deleteConfirm.id);
            }}
          >
            {deleteRequestMutation.isPending ? "Удаление…" : "Удалить"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
      <AlertDialogContent className="z-[230]">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить выбранные заявки РД?</AlertDialogTitle>
          <AlertDialogDescription>
            Будет помечено как удаленное записей: {selectedForBulkDelete.size}. Действие нельзя отменить через интерфейс.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={bulkDeleteRequestsMutation.isPending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={bulkDeleteRequestsMutation.isPending || selectedForBulkDelete.size === 0}
            onClick={(e) => {
              e.preventDefault();
              bulkDeleteRequestsMutation.mutate(Array.from(selectedForBulkDelete));
            }}
          >
            {bulkDeleteRequestsMutation.isPending ? "Удаление..." : `Удалить (${selectedForBulkDelete.size})`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <DocumentParseDialog
      open={documentParseOpen}
      onOpenChange={setDocumentParseOpen}
      title="Распознать координаты из документа"
      coordinateOrder={geometryCoordOrder}
      onReadyText={(text) => {
        setGeometryImportText(text);
        notifyModuleSuccess(
          toast,
          "Текст подставлен в поле импорта. Проверьте и нажмите «Добавить контур».",
        );
      }}
    />
    </>
  );
}

